import { Component, Injectable, OnInit, inject } from "@angular/core";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from "@angular/material/dialog";
import { lastValueFrom } from "rxjs";
import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent,
} from "@tilby/tilby-ui-lib/components/tilby-dialog";
import { FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { TranslateModule } from "@ngx-translate/core";
import { ConfirmDialogService } from "../confirm-dialog";
import { ControlsOf, FieldErrorsComponent } from "src/app/shared/forms";

export type AddPaymentConditionDialogData = { paymentCondition?: { name: string; code: string } };

type PaymentCondition = {
    name: string;
    code: string;
}

@Component({
    selector: "app-tilby-dialog",
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        TranslateModule,
        FieldErrorsComponent,
        TilbyDialogContentComponent,
        TilbyDialogToolbarComponent,
        MatDialogModule
    ],
    templateUrl: "./add-edit-payment-condition-dialog.component.html",
    styleUrls: ["./add-edit-payment-condition-dialog.component.scss"],
})
export class AddEditPaymentConditionDialogComponent implements OnInit {
    protected readonly data: AddPaymentConditionDialogData = inject(MAT_DIALOG_DATA);
    private readonly matDialogRef = inject(MatDialogRef);
    private readonly confirmDialog = inject(ConfirmDialogService);
    private readonly fb = inject(NonNullableFormBuilder);

    addPaymentConditionForm!: FormGroup<ControlsOf<PaymentCondition>>;
    customActions = [
        {
            isIt: () => this.data.paymentCondition !== undefined,
            name:'',
            icon:()=>'delete',
            click:()=> this.deletePaymentCondition()
        },
        {
            isIt: () => true,
            isDisable: () => this.addPaymentConditionForm.invalid,
            name:'',
            icon:()=>'check',
            click:()=> this.confirm()
        }
    ]

    ngOnInit() {
        this.addPaymentConditionForm = this.fb.group({
            name: [this.data.paymentCondition?.name || '', Validators.required],
            code: [this.data.paymentCondition?.code || '', Validators.required],
        });
    }

    protected confirm() {
        // Type Guard
        if(this.addPaymentConditionForm.invalid) {
            this.addPaymentConditionForm.markAllAsTouched();
            return;
        }

        const newPaymentCondition = this.addPaymentConditionForm.getRawValue();

        // Close dialog and return mapped values
        this.matDialogRef.close(newPaymentCondition);
    }

    protected async deletePaymentCondition() {
        const deletePaymentCondition = await this.confirmDialog.openDialog({data: { messageLabel: 'SETTINGS.SETTINGS.PAYMENT_CONDITION_FORM.DELETE_PAYMENT_CONDITION' }});

        if(deletePaymentCondition) {
            this.matDialogRef.close({deletePaymentCondition: true});
        }
    }
}

@Injectable({
    providedIn: "root",
})
export class AddEditPaymentConditionDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    public openDialog(config?: NonNullableConfigData<AddPaymentConditionDialogData>) {
        const data: AddPaymentConditionDialogData = config?.data || { paymentCondition: undefined };
        const configEdited: NonNullableConfigData<AddPaymentConditionDialogData> = {
            ...config,
            ...this.switchMobileDesktopDimensions(),
            disableClose: true,
            data,
        };
        return lastValueFrom(
            this.dialogRef.open(AddEditPaymentConditionDialogComponent, configEdited).afterClosed()
        );
    }
}
